/**
 * Which handover protocol needs the tenant's attention, for surfaces outside
 * the Check-in/out tab (COL-368).
 *
 * The tenant dashboard's tab strip has to show a pulsing "action needed" pip
 * on Check-in/out from the day before move-in (and seven days before
 * move-out) until the protocol is submitted. The card inside that tab already
 * knows the answer; the tab strip has none of its data. This hook re-derives
 * it from the same two queries `ProtocolEntry` runs — same React Query keys,
 * so with the tab open nothing is fetched twice — and the same predicate
 * (`computeProtocolActionNeeded`), so the pip and the card cannot disagree.
 */
import { useMemo } from "react";
import { useActiveBooking, useProtocolsForBooking } from "@/hooks/useProtocol";
import { useNowTick } from "@/hooks/useNowTick";
import {
  computeProtocolActionNeeded,
  type ProtocolAction,
} from "@/lib/protocol-action";

export function useProtocolActionNeeded(): ProtocolAction | null {
  const { data: booking } = useActiveBooking();
  // Until the list has actually loaded, `protocols` is the empty default —
  // which would read as "not started" and flash the pip for a tenant who has
  // in fact submitted. Hold the answer until then.
  const { data: protocols = [], isPending: protocolsPending } =
    useProtocolsForBooking(booking?.id ?? null);
  // A lead-time/window boundary can pass while this stays mounted with no
  // new query data — the tick forces a recompute so it doesn't go stale.
  const now = useNowTick();

  return useMemo(() => {
    if (!booking || protocolsPending) return null;
    return computeProtocolActionNeeded({ booking, protocols, now });
  }, [booking, protocols, protocolsPending, now]);
}
